import React from 'react';
import { TourDefinition } from 'src/app/components/feature-tour/types';

/**
 * Bildirim paneli rehberi (walkthrough). Dock, `registerActions('notification-dock', ...)`
 * ile open/close/expand/collapse/setFilter eylemlerini sunar; adımlar bunlarla
 * arayüzü hazırlar ve kenar düğmesini canlı olarak gösterir.
 * Sürüm artırılırsa tur daha önce görenlere yeniden açılır.
 */
export const NOTIFICATION_DOCK_TOUR_ID = 'notification-dock';

const dock = (ctx: { actions: Record<string, any> }) => ctx.actions[NOTIFICATION_DOCK_TOUR_ID] || {};

export const notificationDockTour: TourDefinition = {
  id: NOTIFICATION_DOCK_TOUR_ID,
  version: 1,
  title: 'Bildirimler',
  autoStart: true,
  autoStartDelayMs: 2500,
  steps: [
    {
      id: 'handle',
      target: '[data-tour="notif-handle"]',
      spotlightPadding: 6,
      title: 'Bildirim düğmesi',
      content: (
        <>
          Zil bildirimleriniz artık her sayfada bu düğmede. Okunmamış bildirim varsa düğme <b>kırmızıya döner</b> ve sayıyı gösterir.
        </>
      ),
      onBefore: async (ctx) => {
        dock(ctx).close?.();
        await ctx.sleep(250);
      },
    },
    {
      id: 'move',
      target: '[data-tour="notif-handle"]',
      spotlightPadding: 6,
      title: 'Düğmeyi istediğiniz yere taşıyın',
      content: (
        <>
          Düğmeyi basılı tutup <b>yukarı-aşağı</b> kaydırabilir, ekranın karşı tarafına atarak <b>sol ya da sağ kenara</b> alabilirsiniz. Böylece çalıştığınız alanı kapatmaz.
          Konumu hatırlanır.
          <br />
          <br />
          <i>Şimdi gösteriyoruz: önce yukarı-aşağı, sonra karşı kenara gidip geri dönüyor.</i>
        </>
      ),
      onEnter: async (ctx) => {
        await ctx.sleep(500);
        await dock(ctx).demoDrag?.();
      },
    },
    {
      id: 'open',
      target: '[data-tour="notif-sheet"]',
      spotlightPadding: 0,
      title: 'Panel kenardan açılır',
      content: (
        <>
          Düğmeye tıklayınca panel, düğmenin bulunduğu kenardan kayarak açılır.
          <br />
          <br />
          Kapatmak için arka plana tıklayın, <kbd>Esc</kbd> basın ya da paneli <b>kenara doğru sürükleyin</b>.
        </>
      ),
      onBefore: async (ctx) => {
        dock(ctx).open?.();
        await ctx.sleep(650);
      },
    },
    {
      id: 'filters',
      target: '[data-tour="notif-filters"]',
      title: 'Filtreler',
      content: (
        <>
          <b>Tümü</b>, <b>Okunmamış</b>, <b>Bana özel</b> ve <b>Genel</b> çipleriyle listeyi daraltın. Okunmamış çipindeki sayı rozetle aynıdır.
        </>
      ),
    },
    {
      id: 'item',
      target: '[data-tour="notif-item"]',
      fallbackTarget: '[data-tour="notif-list"]',
      title: 'Okundu / okunmadı',
      content: (
        <>
          Bir satıra tıklayınca bildirim <b>okundu</b> olur. Sağdaki zarf düğmesiyle istediğiniz zaman <b>okunmadı</b>ya geri alabilirsiniz.
          <br />
          <br />
          Mavi nokta size özel, mor nokta herkese gönderilen genel bildirimi gösterir.
        </>
      ),
    },
    {
      id: 'mark-all',
      target: '[data-tour="notif-mark-all"]',
      title: 'Tümünü okundu işaretle',
      content: <>Biriken bildirimleri tek tıkla temizler; rozet sıfırlanır.</>,
    },
    {
      id: 'edge',
      target: '[data-tour="notif-edge"]',
      spotlightPadding: 6,
      placement: 'auto',
      title: 'Genişlet / daralt',
      content: (
        <>
          Kenardaki mavi düğme paneli <b>tam ekrana yakın</b> genişletir; tekrar tıklayınca daraltır. Ok, panelin gideceği yönü gösterir.
          <br />
          <br />
          Aynı düğmeyi <b>sürükleyerek</b> de yapabilirsiniz. Şimdi bir kez gösteriyoruz.
        </>
      ),
      onEnter: async (ctx) => {
        await ctx.sleep(700);
        dock(ctx).expand?.();
        await ctx.sleep(1600);
        dock(ctx).collapse?.();
      },
    },
    {
      id: 'live',
      title: 'Canlı bildirimler',
      content: (
        <>
          Yeni bir bildirim geldiğinde zil sallanır, kısa bir uyarı çıkar ve liste anında güncellenir; sayfayı yenilemeniz gerekmez.
          <br />
          <br />
          Bu rehberi istediğinizde panel başlığındaki <b>?</b> düğmesinden yeniden izleyebilirsiniz.
        </>
      ),
    },
  ],
  onFinish: (ctx) => {
    dock(ctx).close?.();
  },
};

export default notificationDockTour;
